import React from 'react'
import { Box, alpha, useMediaQuery, useTheme } from '@mui/material'

import { brandRampStops } from '../authFieldChrome'

/**
 * The signup success mark: the Vera V held inside a slow tap pulse, resolving
 * when an iris closes around it and a check writes itself.
 *
 * ── What this replaced, and why ───────────────────────────────────────────
 *
 * The previous version filled a disc with the brand gradient and popped it to
 * 1.06 as five motes spiralled in. Spencer's read: *"the bright purple looks
 * gamey"*. That was right, and it was two things rather than one — a large
 * evenly-saturated FILL, and an OVERSHOOT. Overshoot is the grammar of reward
 * toasts and achievement badges; at 130px of saturated purple it does not read
 * as premium no matter which purple it is. Both are gone. Nothing is filled and
 * nothing overshoots.
 *
 * Chosen from five candidates as a combination of three of them:
 *   - the restraint (no fill, hairline, one slow check stroke)
 *   - a LARGER, SLOWER pulse — 2.6x over 4s, three rings, against the previous
 *     1.65x over 2.6s with two ("the pulsing can be bigger")
 *   - an IRIS that closes on success, in the brand gradient
 *
 * ── Lineage ───────────────────────────────────────────────────────────────
 *
 * The pulse is `ScanTag`'s (`apps/app/.../productPage/ScanTag.tsx`) — the tap
 * animation the consumer app already uses when a phone is held to a tag —
 * enlarged and slowed. The V is `LogoLoader`'s `VERA_MARK_PATH`, reused
 * verbatim. Deliberately NOT extracted to `@repo/ui` yet: per §36 the trigger
 * is the second hand-roll, and this is still the first combination of them.
 *
 * ── The state is not the animation ────────────────────────────────────────
 *
 * `phase` drives which elements are present and what their target values are;
 * CSS does the interpolating. If no animation ever runs — reduced motion, a
 * dropped frame budget, or a preview surface with no animation clock — the
 * `done` phase still renders a closed iris and a complete check. Nothing about
 * reaching a usable screen depends on a transition firing, which is why the
 * page jumps straight to `done` under reduced motion rather than running the
 * beats faster.
 */

/** The Vera mark, on its own 172x148 artboard. Copied from `LogoLoader`. */
const VERA_MARK_PATH =
  'M84.0345 79.33L39.6849 1.77528C39.3364 1.17763 38.6829 0.802368 37.9568 0.802368H2.39299C0.897245 0.802368 -0.0466709 2.34512 0.679419 3.59601L84.0345 147.031C84.7896 148.323 86.721 148.323 87.4616 147.031L170.817 3.59601C171.543 2.34512 170.599 0.802368 169.103 0.802368H133.539C132.828 0.802368 132.16 1.17763 131.811 1.77528L87.4616 79.33C86.721 80.6365 84.7606 80.6365 84.02 79.33H84.0345Z'

/**
 * `closing` is the iris sweep, `check` the draw-on. They are cumulative — an
 * element at `check` is also past `closing` — so each style below tests for
 * "at or past" its beat, never equality.
 */
export type SignupSuccessMarkPhase = 'waiting' | 'closing' | 'check' | 'done'

const ORDER: SignupSuccessMarkPhase[] = ['waiting', 'closing', 'check', 'done']
const atLeast = (phase: SignupSuccessMarkPhase, beat: SignupSuccessMarkPhase) =>
  ORDER.indexOf(phase) >= ORDER.indexOf(beat)

export interface SignupSuccessMarkProps {
  phase: SignupSuccessMarkPhase
  /** Stage diameter in px. The ring, the pulse and both glyphs scale from this. */
  size?: number
  /**
   * `error` turns the whole mark red and resolves to an X instead of a check —
   * the failure screens get the same pulse and the same iris, so the two
   * outcomes are recognisably one family rather than two unrelated screens.
   *
   * The failure iris is a FLAT red, not a gradient. There is no canonical red
   * ramp in the palette, and inventing one for a single surface would put a
   * second gradient vocabulary in the product; flat also reads more plainly,
   * which is right for the one screen that has to be unambiguous.
   */
  tone?: 'brand' | 'error'
}

/** The X, as two crossing strokes on the same 24x24 grid as the check. */
const X_PATHS = ['M6 6 L18 18', 'M18 6 L6 18']
const X_STROKE_LENGTH = 17

/** The iris circle in its own 100x100 viewBox, and the dash length that covers it. */
const IRIS_R = 46
const IRIS_CIRCUMFERENCE = 2 * Math.PI * IRIS_R

/**
 * The iris ramp is MODE-AWARE, and it has to be.
 *
 * The canonical brand gradient (`PROGRESS_BAR_GRADIENT`, rgb(130,90,195) ->
 * rgb(55,39,137)) is calibrated for a FILLED BAR on a light track. Measured as
 * a 1.6px ring against the dark page (#09090b): the light end is 3.98:1 and the
 * dark end is 1.73:1 — under the 3:1 minimum for a graphical object. The
 * bottom-right of the ring visibly dissolved and the iris read as unfinished.
 *
 * Dark therefore runs the ramp between two LIGHT purples instead. Same brand,
 * same direction of travel, inverted lightness — the same reversal
 * `AuthPrimaryButton` records for its own fill, for the same reason: a value
 * calibrated against one page colour does not survive the other.
 *
 * THE STOPS NO LONGER LIVE HERE. They were private to this file (`IRIS_STOPS`)
 * until the step rail needed the same ramp; that second use is §36's trigger, so
 * they moved to `authFieldChrome`'s `brandRampStops` where both read them.
 *
 * One value CHANGED in the move, and it is worth stating rather than burying:
 * dark's far stop went rgb(127,72,223) -> rgb(140,95,230). An earlier revision of
 * this comment claimed the ramp's measurements were "all clear of the 4.5:1 bar",
 * while quoting 3.69:1 for that stop. Both halves cannot be true. 3.69:1 clears
 * the 3:1 a 2px stroke actually answers to, so the mark was never broken — the
 * CLAIM was wrong, not the colour. The rail's numeral does answer to 4.5:1
 * though, so the shared stop was lifted to satisfy the stricter of the two uses,
 * and this mark inherits a slightly lighter, slightly higher-contrast far end
 * (3.69:1 -> 4.64:1 against the page) as a side effect.
 */

/**
 * One instance renders at a time (this mark appears once, on one screen), so a
 * stable id is safe. If a second is ever mounted alongside, this must become
 * unique per instance — two `<linearGradient>` elements sharing an id means the
 * first one found wins for both.
 */
const IRIS_GRADIENT_ID = 'veraSuccessIris'

export const SignupSuccessMark = ({ phase, size = 132, tone = 'brand' }: SignupSuccessMarkProps) => {
  const theme = useTheme()
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const closing = atLeast(phase, 'closing')
  const checked = atLeast(phase, 'check')
  const isError = tone === 'error'

  // `brand[600]` with `primary.main` as the fallback — the same resolution
  // ScanTag's `brandColor` helper makes, kept inline because it is one lookup.
  const brand = theme.palette.brand?.[600] ?? theme.palette.primary.main
  /** What the pulse and the track are drawn in. The iris follows separately. */
  const accent = isError ? theme.palette.error.main : brand
  const [irisFrom, irisTo] = brandRampStops(theme)
  const glyphSize = Math.round(size * 0.3)

  return (
    <Box
      // `role="img"` + a label, because this mark is the primary carrier of
      // "it worked" on this screen. The heading beneath says so too, so the
      // label is deliberately terse rather than duplicating the sentence.
      role="img"
      aria-label={
        checked ? (isError ? "Payment couldn't be confirmed" : 'Account ready') : 'Confirming'
      }
      sx={{
        position: 'relative',
        width: `${size}px`,
        height: `${size}px`,
        display: 'grid',
        placeItems: 'center',
        flex: '0 0 auto',
      }}
    >
      {/* THE TAP PULSE — ScanTag's rings, enlarged and slowed.
          2.6x over 4s with three rings on a 1.35s stagger, against the previous
          1.65x over 2.6s with two. At any instant one ring is near the mark and
          another is most of the way out, so the field reads as continuous
          rather than as a repeating ping.
 */}
      {/* THE PULSE STOPS once the outcome is known.
          It used to run forever, on the argument that a halting pulse reads as
          the animation breaking. That holds while the mark is WAITING — it is
          what says "still working" — but past the resolution it is the only
          thing still moving on a screen whose whole job is now to be read, and
          it keeps pulling the eye off the check. Faded rather than cut so the
          rings do not vanish mid-flight; the keyframes own each ring's own
          opacity, so the stop has to be a wrapper multiplying them. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: checked ? 0 : 1,
          transition: reduceMotion ? 'none' : 'opacity 520ms ease-out',
        }}
      >
      {[0, 1.35, 2.7].map((delay) => (
        <Box
          key={delay}
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `1px solid ${accent}`,
            opacity: 0.38,
            animation: 'veraSignupPulse 4s cubic-bezier(0.2, 0.6, 0.3, 1) infinite',
            animationDelay: `${delay}s`,
            '@keyframes veraSignupPulse': {
              '0%': { transform: 'scale(1)', opacity: 0.38 },
              '100%': { transform: 'scale(2.6)', opacity: 0 },
            },
            // Only the FIRST ring survives with motion off — the same reasoning
            // ScanTag records: rings pinned at scale(1) double-paint on the
            // circle's own edge and read as one heavy border.
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              opacity: delay === 0 ? 0.38 : 0,
            },
          }}
        />
      ))}
      </Box>

      {/* THE TINT — the same translucent fill the app already uses for a
          selectable/hovered surface (`alpha(brand[500], 0.06)`, FeaturesPage),
          and its red counterpart at the same weight for the failure tone so the
          two read as one treatment rather than two.

          This is NOT the saturated disc that was removed: 6% is a wash you read
          as "this circle is a surface", where the old fill was 100% brand and
          read as a badge. It gives the glyph something to sit ON so the mark
          stops looking like a stroke floating over the page.

          Inside the iris radius (r=46 of 50, so inset ~4/50 of the box) so the
          fill stops at the ring rather than bleeding past it. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: `${size * 0.04}px`,
          borderRadius: '50%',
          backgroundColor: alpha(accent, 0.06),
        }}
      />

      {/* THE TINT + THE TRACK + THE IRIS.

          The track is the faint circle the mark sits in while waiting. The iris
          is the same circle drawn over it in the brand gradient, revealed by
          `stroke-dashoffset` so it sweeps closed rather than fading in.

          Rotated -90deg so the sweep STARTS AT THE TOP, which is where the eye
          already is, and the gradient is angled so its light end lands there
          too. */}
      <Box
        aria-hidden
        component="svg"
        viewBox="0 0 100 100"
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transform: 'rotate(-90deg)' }}
      >
        <defs>
          <linearGradient id={IRIS_GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={irisFrom} />
            <stop offset="100%" stopColor={irisTo} />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r={IRIS_R} fill="none" stroke={alpha(accent, 0.16)} strokeWidth="1" />
        {/* The gradient MUST be an SVG paint server referenced by `stroke` — a
            CSS gradient is not a valid stroke value, so there is no way to do
            this with `background`. */}
        <Box
          component="circle"
          cx="50"
          cy="50"
          r={IRIS_R}
          sx={{
            fill: 'none',
            stroke: isError ? accent : `url(#${IRIS_GRADIENT_ID})`,
            strokeWidth: 1.6,
            strokeLinecap: 'round',
            strokeDasharray: IRIS_CIRCUMFERENCE,
            strokeDashoffset: closing || reduceMotion ? 0 : IRIS_CIRCUMFERENCE,
            transition: reduceMotion
              ? 'none'
              : 'stroke-dashoffset 1100ms cubic-bezier(0.65, 0, 0.35, 1)',
          }}
        />
      </Box>

      {/* THE V — present until the check takes over. It leaves on the CHECK
          beat rather than on `closing`, so the iris closes AROUND the mark
          instead of around an empty circle. */}
      <Box
        aria-hidden
        component="svg"
        viewBox="0 0 172 148"
        sx={{
          position: 'absolute',
          width: `${glyphSize}px`,
          height: `${Math.round(glyphSize * (148 / 172))}px`,
          fill: isError ? accent : theme.palette.text.primary,
          opacity: checked ? 0 : 1,
          transform: checked ? 'scale(0.92)' : 'scale(1)',
          transition: reduceMotion
            ? 'none'
            : 'opacity 500ms ease-out, transform 700ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <path d={VERA_MARK_PATH} />
      </Box>

      {/* THE CHECK — one slow stroke, in INK rather than brand.

          Ink, not purple, is the whole point of the restraint: the iris already
          carries the brand, and a second coloured element turns a resolution
          into a celebration. It also means the check needs no contrast
          calculation of its own — `text.primary` is the page's own foreground
          and is correct in both modes by construction. */}
      <Box
        aria-hidden
        component="svg"
        viewBox="0 0 24 24"
        sx={{
          position: 'absolute',
          width: `${Math.round(glyphSize * 1.15)}px`,
          height: `${Math.round(glyphSize * 1.15)}px`,
          fill: 'none',
          // The check takes the SAME gradient the iris does, rather than ink.
          // The two are the only marks left on the screen and they now read as
          // one object — a ring and the stroke inside it, in one material —
          // where an ink check against a purple iris read as two unrelated
          // parts. Contrast is unchanged by this: the ramp measures 5.0:1 and
          // 11.5:1 in light, 7.2:1 and 3.69:1 in dark (see IRIS_STOPS), all
          // clear of the 4.5:1 bar at a 2px stroke.
          //
          // The X stays FLAT red — see the `tone` prop's own note.
          stroke: isError ? accent : `url(#${IRIS_GRADIENT_ID})`,
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          opacity: checked ? 1 : 0,
          transition: reduceMotion ? 'none' : 'opacity 300ms ease-out',
          '& path': {
            strokeDasharray: isError ? X_STROKE_LENGTH : 20,
            strokeDashoffset: checked || reduceMotion ? 0 : isError ? X_STROKE_LENGTH : 20,
            transition: reduceMotion
              ? 'none'
              : 'stroke-dashoffset 820ms cubic-bezier(0.65, 0, 0.35, 1)',
          },
          // The X's second stroke follows the first rather than racing it —
          // two lines drawing simultaneously read as a glitch, one after the
          // other reads as a cross being struck.
          '& path:last-of-type': {
            transitionDelay: reduceMotion ? '0ms' : '180ms',
          },
        }}
      >
        {isError ? (
          X_PATHS.map((d) => <path key={d} d={d} />)
        ) : (
          <path d="M5 12.5 L10 17.5 L19 7" />
        )}
      </Box>
    </Box>
  )
}

export default SignupSuccessMark
